import type { IconButtonProps } from '@mui/material/IconButton';
import type { TTabs, TNotify, NotifyType } from 'src/@types/notification';

import { toast } from 'sonner';
import { m } from 'framer-motion';
import { useNavigate } from 'react-router';
import { useMemo, useState, useEffect, useCallback } from 'react';

import {
  Tab,
  Box,
  Stack,
  Badge,
  Drawer,
  Button,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';

import { useBoolean } from 'src/hooks/useBoolean';

import { Svg } from 'src/assets/icons/Svg';
import {
  useMarkNotificationRead,
  useMarkNotificationsReadBulk,
} from 'src/queries/useMyNotifications';

import { Label } from 'src/components/label';
import { varHover } from 'src/components/animate';
import { Scrollbar } from 'src/components/scrollbar';
import { CustomTabs } from 'src/components/custom-tabs';

import { useAuthContext } from 'src/auth/hooks';

import { NotificationItem } from './notification-item';

export type NotificationsDrawerProps = IconButtonProps & {
  data?: TNotify[];
};

export function NotificationsDrawer({ data = [], sx, ...other }: NotificationsDrawerProps) {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const drawer = useBoolean();

  const markMany = useMarkNotificationsReadBulk();
  const markOne = useMarkNotificationRead();

  const [currentTab, setCurrentTab] = useState<TTabs['value']>('all');
  const [tabsElement, setTabElement] = useState<TTabs[]>([
    { value: 'all', label: 'Tất cả', count: 0 },
    { value: 'unread', label: 'Đã đọc', count: 0 },
    { value: 'archived', label: 'Chưa đọc', count: 0 },
  ]);
  const filterByTab = (list: TNotify[], tab: TTabs['value']) => {
    switch (tab) {
      case 'unread':
        return list.filter((i) => i.isRead);
      case 'archived':
        return list.filter((i) => !i.isRead);
      default:
        return list;
    }
  };

  const filteredNotifications = useMemo(() => filterByTab(data, currentTab), [data, currentTab]);

  useEffect(() => {
    filterByTab(data, currentTab);
  }, [currentTab, data]);

  const handleChangeTab = useCallback((_e: React.SyntheticEvent, newValue: TTabs['value']) => {
    setCurrentTab(newValue);
  }, []);

  const handleClickNotify = useCallback(
    (id: string, type: NotifyType, content: string) => {
      markOne.mutate({ id, read: true });
      if (type === 'message') {
        try {
          const parsed = JSON.parse(content ?? '{}');
          const conversationId = parsed?.conversationId;

          if (conversationId) {
            navigate(`/${user?.role ?? 'client'}/chat?id=${encodeURIComponent(conversationId)}`);
            drawer.onFalse();
          } else {
            toast.error('Missing conversationId in content', parsed);
          }
        } catch (err) {
          toast.error('Invalid JSON content');
        }
      } else {
        navigate(`/${user?.role ?? 'client'}/notify?id=${encodeURIComponent(id)}`);
        drawer.onFalse();
      }
    },
    [drawer, markOne, navigate, user?.role]
  );

  const handleToNotify = useCallback(() => {
    navigate(`/${user?.role ?? 'client'}/notify`);
    drawer.onFalse();
  }, [drawer, navigate, user?.role]);

  useEffect(() => {
    const totalNotify = data.length;
    const totalUnread = data.filter((item) => item.isRead).length;
    const totalArchived = data.filter((item) => !item.isRead).length;
    setTabElement([
      { value: 'all', label: 'Tất cả', count: totalNotify },
      { value: 'unread', label: 'Đã đọc', count: totalUnread },
      { value: 'archived', label: 'Chưa đọc', count: totalArchived },
    ]);
  }, [data]);

  const totalUnRead = data.filter((item) => item.isRead === false).length;

  const handleMarkAllAsRead = () => {
    const ids = filterByTab(data, 'archived').map((id) => id.id);
    markMany.mutate({ ids, read: true });
  };

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <IconButton onClick={drawer.onFalse}>{Svg('icon-close')}</IconButton>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        THÔNG BÁO
      </Typography>
      {!!totalUnRead && (
        <Tooltip title="Xem tất cả">
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            {Svg('icon-done-all')}
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  const renderTabs = (
    <CustomTabs variant="fullWidth" value={currentTab} onChange={handleChangeTab}>
      {tabsElement.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={((tab.value === 'all' || tab.value === currentTab) && 'filled') || 'soft'}
              color={
                (tab.value === 'unread' && 'info') ||
                (tab.value === 'archived' && 'success') ||
                'default'
              }
            >
              {tab.count}
            </Label>
          }
        />
      ))}
    </CustomTabs>
  );

  const renderList = (
    <Scrollbar>
      <Box component="ul">
        {filteredNotifications.map((notification) => (
          <Box
            component="li"
            onClick={() =>
              handleClickNotify(notification.id, notification.type, notification.content)
            }
            key={notification.id}
            sx={{ display: 'flex' }}
          >
            <NotificationItem notification={notification} />
          </Box>
        ))}
      </Box>
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={drawer.onTrue}
        sx={sx}
        {...other}
      >
        <Badge badgeContent={totalUnRead} color="error">
          {Svg('icon-notification')}
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{ sx: { width: 1, maxWidth: 420 } }}
      >
        {renderHead}

        {renderTabs}

        {renderList}

        <Box sx={{ p: 1 }}>
          <Button fullWidth size="large" onClick={handleToNotify}>
            Xem tất cả
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
